import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Circle, Send } from 'lucide-react';
import { OnboardingProgress } from '../types';
import { api } from '../utils/api';
import { useTranslation } from '../i18n/translations';

interface SmartOnboardingProps {
  campaignCount: number;
  onCreateCampaign: () => void;
  onOpenSaved: () => void;
}

const EMPTY: OnboardingProgress = { businessType: '', clientName: '', projectName: '', firstCampaignCreated: false, firstCampaignSaved: false, firstCampaignSent: false, dismissed: false };

export const SmartOnboarding: React.FC<SmartOnboardingProps> = ({ campaignCount, onCreateCampaign, onOpenSaved }) => {
  const { tx } = useTranslation();
  const [progress, setProgress] = useState<OnboardingProgress>(EMPTY);
  const [open, setOpen] = useState(true);
  useEffect(() => { api.getOnboarding().then((result) => setProgress(result?.progress || EMPTY)).catch(() => setProgress(EMPTY)); }, [campaignCount]);
  const steps = useMemo(() => [
    { key: 'businessType', label: tx('اختيار نوع النشاط'), done: !!progress.businessType },
    { key: 'clientName', label: tx('إنشاء أول عميل'), done: !!progress.clientName },
    { key: 'projectName', label: tx('إدخال أول مشروع'), done: !!progress.projectName },
    { key: 'firstCampaignCreated', label: tx('توليد أول حملة'), done: progress.firstCampaignCreated || campaignCount > 0 },
    { key: 'firstCampaignSaved', label: tx('حفظ الحملة'), done: progress.firstCampaignSaved || campaignCount > 0 },
    { key: 'firstCampaignSent', label: tx('إرسالها إلى العميل'), done: progress.firstCampaignSent },
  ], [progress, campaignCount, tx]);
  const completed = steps.filter((step) => step.done).length;
  const percent = Math.round(completed / steps.length * 100);
  const save = async (patch: Partial<OnboardingProgress>) => {
    const result = await api.updateOnboarding(patch);
    if (result?.progress) setProgress(result.progress);
    else setProgress((current) => ({ ...current, ...patch }));
  };
  if (progress.dismissed || percent === 100) return null;
  return (
    <section className="mb-8 relative overflow-hidden bg-gradient-to-l from-brand-900 to-brand-800 rounded-2xl p-6 md:p-8 text-white shadow-xl">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      
      <div className="relative z-10 flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-brand-500/30 text-brand-100 text-xs font-bold px-2.5 py-1 rounded-md border border-brand-400/30">
              خطوات الإعداد
            </span>
            <span className="text-sm font-medium text-brand-200">
              {percent}% مكتمل
            </span>
          </div>
          <h2 className="text-2xl font-black mb-2 leading-tight text-white">مرحباً بك في مساحة عملك!</h2>
          <p className="text-brand-100 text-sm md:text-base leading-relaxed max-w-lg">
            دعنا نجهز حسابك لإطلاق حملات ناجحة. إكمال هذه الخطوات سيساعدنا في تخصيص الذكاء الاصطناعي ليلائم نشاطك بشكل أدق.
          </p>
        </div>

        <div className="w-full md:w-auto shrink-0 flex flex-col gap-3 min-w-[280px]">
          <div className="bg-white/10 border border-white/20 p-4 rounded-xl flex flex-col gap-3 backdrop-blur-md">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-brand-100">نوع نشاطك الأساسي</span>
              <select 
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-brand-300 transition-colors"
                value={progress.businessType} 
                onChange={(e) => save({ businessType: e.target.value })}
              >
                <option value="" className="text-slate-800">اختر المجال</option>
                <option value="agency" className="text-slate-800">وكالة تسويق</option>
                <option value="developer" className="text-slate-800">مطور عقاري</option>
                <option value="broker" className="text-slate-800">شركة وساطة</option>
                <option value="ecommerce" className="text-slate-800">متجر إلكتروني</option>
                <option value="freelancer" className="text-slate-800">مسوّق مستقل</option>
              </select>
            </label>
            
            <button 
              onClick={campaignCount ? onOpenSaved : onCreateCampaign}
              className="w-full bg-brand-400 hover:bg-brand-500 text-brand-950 font-black py-2.5 rounded-lg transition-colors shadow-sm mt-1"
            >
              {campaignCount ? tx('متابعة حملاتك') : tx('إنشاء أول حملة الآن')}
            </button>
          </div>
          
          <button 
            onClick={() => save({ dismissed: true })}
            className="text-xs font-medium text-brand-300 hover:text-white transition-colors self-center flex items-center gap-1.5"
          >
            تخطي الإعدادات
          </button>
        </div>
      </div>
    </section>
  );
};
